import {
  type AiActionCapabilityV1,
  type AiActionFlagsV1,
  type AiActionIdV1,
  type AiActionPolicyV1,
  AiActionPolicyV1Schema,
  type SmartCreationReviewBidBoundsSuggestion,
} from "@adpilot/shared";
import { IconChevronRight } from "@tabler/icons-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MoneyInput } from "@/components/patterns/MoneyInput";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const groups: ReadonlyArray<{
  key: "structure" | "bidding" | "budget" | "targeting";
  actions: readonly AiActionIdV1[];
}> = [
  { key: "structure", actions: ["pauseCampaign"] },
  { key: "bidding", actions: ["baseBid", "placementBid"] },
  { key: "budget", actions: ["budgetBoost"] },
  { key: "targeting", actions: ["harvest", "negateSource", "negation", "pauseAndSupplement"] },
];
const bidActions: readonly AiActionIdV1[] = [
  "baseBid",
  "placementBid",
  "harvest",
  "pauseAndSupplement",
];

export const previewAiGroups: ReadonlyArray<{
  key: string;
  actions: readonly AiActionIdV1[];
}> = [
  { key: "pause", actions: ["pauseCampaign"] },
  { key: "bidding", actions: ["baseBid", "placementBid"] },
  { key: "boost", actions: ["budgetBoost"] },
  { key: "harvest", actions: ["harvest"] },
  { key: "negative", actions: ["negateSource", "negation", "pauseAndSupplement"] },
];

interface AiActionPolicyFieldsProps {
  policy?: AiActionPolicyV1;
  capabilities?: readonly AiActionCapabilityV1[];
  capabilityStatus?: "loading" | "ready" | "unavailable";
  disabled?: boolean;
  onPolicyChange?: (policy: AiActionPolicyV1) => Promise<boolean>;
  variant?: "default" | "preview";
  immediate?: boolean;
  onInvalidChange?: (invalid: boolean) => void;
  initialExpandedGroup?: string;
  bidBoundsSuggestion?: SmartCreationReviewBidBoundsSuggestion;
  onDirtyChange?: (dirty: boolean) => void;
  onSaved?: () => void;
  onSavingChange?: (saving: boolean) => void;
  onCancel?: () => void;
  onRetryCapabilities?: () => void;
}

interface PolicyEdit {
  actions: AiActionFlagsV1 | undefined;
  minimumBid: string;
  maximumBid: string;
  boostLimit: string;
  boostDuration: string;
  harvestExclusions: string;
  negativeExclusions: string;
}

function policyEdit(
  policy?: AiActionPolicyV1,
  suggestion?: SmartCreationReviewBidBoundsSuggestion,
): PolicyEdit {
  return {
    actions: policy?.actions,
    minimumBid: (policy?.bidBounds?.min ?? suggestion?.min)?.toString() ?? "",
    maximumBid: (policy?.bidBounds?.max ?? suggestion?.max)?.toString() ?? "",
    boostLimit: (policy?.budgetBoostPercentLimit ?? 10).toString(),
    boostDuration: (policy?.budgetBoostDurationDays ?? 1).toString(),
    harvestExclusions: policy?.harvestExclusions.join("\n") ?? "",
    negativeExclusions: policy?.negativeExclusions.join("\n") ?? "",
  };
}

function PreviewPolicyFields({
  policy,
  capabilities,
  capabilityStatus = capabilities ? "ready" : "unavailable",
  disabled,
  onPolicyChange,
  initialExpandedGroup,
  bidBoundsSuggestion,
  immediate = false,
  onInvalidChange,
  onDirtyChange,
  onSaved,
  onSavingChange,
  onCancel,
  onRetryCapabilities,
}: AiActionPolicyFieldsProps) {
  const { t } = useTranslation("smartCreation");
  const fieldPrefix = useId();
  const [edit, setEdit] = useState(() => policyEdit(policy, bidBoundsSuggestion));
  const [errors, setErrors] = useState<ReadonlySet<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [expanded, setExpanded] = useState<string[]>(
    initialExpandedGroup ? [initialExpandedGroup] : [],
  );
  const dirtyCallback = useRef(onDirtyChange);
  const dirty = JSON.stringify(edit) !== JSON.stringify(policyEdit(policy, bidBoundsSuggestion));
  const editable = !!policy && !!onPolicyChange && !disabled && !saving;
  const canEditAction = (action: AiActionIdV1) => {
    const matches = capabilities?.filter((item) => item.action === action) ?? [];
    return (
      editable &&
      capabilityStatus === "ready" &&
      matches.length === 1 &&
      matches[0]?.state === "supported"
    );
  };
  const canEditBids = bidActions.some(canEditAction);
  const canEditBoost = canEditAction("budgetBoost");

  useEffect(() => {
    setEdit(policyEdit(policy, bidBoundsSuggestion));
    setErrors(new Set());
  }, [policy, bidBoundsSuggestion]);
  useEffect(() => {
    onDirtyChange?.(immediate ? false : dirty);
  }, [dirty, immediate, onDirtyChange]);
  useEffect(() => {
    dirtyCallback.current = onDirtyChange;
  }, [onDirtyChange]);
  useEffect(() => () => dirtyCallback.current?.(false), []);

  function update<K extends keyof PolicyEdit>(key: K, value: PolicyEdit[K]) {
    const next = { ...edit, [key]: value };
    setEdit(next);
    if (immediate) void save(next);
  }

  function cancel() {
    setEdit(policyEdit(policy, bidBoundsSuggestion));
    setErrors(new Set());
    setSaveError(false);
    onCancel?.();
  }

  async function save(nextEdit = edit) {
    if (!policy || !onPolicyChange || !editable || !nextEdit.actions) return;
    const usesBids = bidActions.some((action) => nextEdit.actions?.[action]);
    const parseBounds =
      usesBids || nextEdit.minimumBid.trim() !== "" || nextEdit.maximumBid.trim() !== "";
    const parsed = AiActionPolicyV1Schema.safeParse({
      ...policy,
      revision: policy.revision + 1,
      actions: {
        ...nextEdit.actions,
        rapidTesting: false,
        bidDayparting: false,
        budgetDayparting: false,
        reallocation: false,
      },
      bidBounds: parseBounds
        ? { min: Number(nextEdit.minimumBid), max: Number(nextEdit.maximumBid) }
        : undefined,
      budgetBoostPercentLimit: nextEdit.boostLimit.trim() ? Number(nextEdit.boostLimit) : undefined,
      budgetBoostDurationDays: Number(nextEdit.boostDuration),
      harvestExclusions: exclusionsFromText(nextEdit.harvestExclusions),
      negativeExclusions: exclusionsFromText(nextEdit.negativeExclusions),
    });
    if (!parsed.success) {
      const nextErrors = new Set(parsed.error.issues.map((issue) => String(issue.path[0])));
      // Realtime validation must respect sections the user has deliberately collapsed.
      if (!immediate) {
        if (nextErrors.has("bidBounds"))
          setExpanded((values) => [...new Set([...values, "bidding"])]);
        if ([...nextErrors].some((key) => key.startsWith("budgetBoost")))
          setExpanded((values) => [...new Set([...values, "boost"])]);
        if (nextErrors.has("harvestExclusions"))
          setExpanded((values) => [...new Set([...values, "harvest"])]);
        if (nextErrors.has("negativeExclusions"))
          setExpanded((values) => [...new Set([...values, "negative"])]);
      }
      onInvalidChange?.(true);
      setErrors(nextErrors);
      return;
    }
    onInvalidChange?.(true);
    setErrors(new Set());
    setSaveError(false);
    setSaving(true);
    onSavingChange?.(true);
    try {
      if (await onPolicyChange(parsed.data)) {
        onInvalidChange?.(false);
        onDirtyChange?.(false);
        if (!immediate) onSaved?.();
      } else setSaveError(true);
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  }

  function actionField(action: AiActionIdV1) {
    const available = canEditAction(action);
    return (
      <Field
        key={action}
        orientation="horizontal"
        data-disabled={!available || undefined}
        className="items-start gap-2.5"
      >
        <Checkbox
          id={`${fieldPrefix}preview-ai-${action}`}
          checked={edit.actions?.[action] ?? false}
          disabled={!available}
          onCheckedChange={(checked) =>
            edit.actions && update("actions", { ...edit.actions, [action]: checked })
          }
        />
        <div className="flex min-w-0 flex-col gap-0.5">
          <FieldLabel htmlFor={`${fieldPrefix}preview-ai-${action}`} className="text-xs leading-5">
            {t(`review.aiActions.${action}`)}
          </FieldLabel>
          <FieldDescription className="text-xs leading-5">
            {t(`review.preview.ai.description.${action}`)}
          </FieldDescription>
          {!available && capabilityStatus === "ready" && !disabled ? (
            <p className="text-[11px] leading-4 text-muted-foreground">
              {t("review.preview.ai.actionUnavailable")}
            </p>
          ) : null}
        </div>
      </Field>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3" data-preview-ai-fields>
      <Accordion
        multiple
        value={expanded}
        onValueChange={(value) => setExpanded(value.map(String))}
        className="overflow-hidden rounded-[10px] border border-line"
      >
        {previewAiGroups.map((group) => {
          const count = group.actions.filter((action) => edit.actions?.[action]).length;
          return (
            <AccordionItem key={group.key} value={group.key}>
              <AccordionTrigger className="items-center gap-3 rounded-none px-5 py-1.5 [&>[data-slot=accordion-indicator]]:hidden">
                <span className="flex min-w-0 flex-1 flex-col gap-px">
                  <span className="text-xs font-medium leading-[18px]">
                    {t(`review.preview.ai.group.${group.key}`)}
                  </span>
                  <span className="text-[11px] font-normal leading-[18px] text-muted-foreground">
                    {t(`review.preview.ai.summary.${group.key}`)}
                  </span>
                </span>
                {capabilityStatus === "loading" ? (
                  <Spinner />
                ) : (
                  <Badge
                    variant={count === group.actions.length ? "success" : "neutral"}
                    size="compact"
                    className="h-[22px] px-2 text-[11px] font-normal"
                  >
                    {count === group.actions.length
                      ? t("review.preview.ai.allowed")
                      : count === 0
                        ? t("review.preview.ai.notAllowed")
                        : t("review.preview.ai.allowedCount", {
                            count,
                            total: group.actions.length,
                          })}
                  </Badge>
                )}
                <IconChevronRight
                  size={14}
                  stroke={1.8}
                  className={cn(
                    "shrink-0 text-muted-foreground",
                    expanded.includes(group.key) && "rotate-90",
                  )}
                />
              </AccordionTrigger>
              <AccordionPanel className="flex flex-col gap-3 px-5 pb-5 pt-3">
                <FieldGroup className="gap-3">{group.actions.map(actionField)}</FieldGroup>
                {group.key === "bidding" ? (
                  <FieldGroup className="grid grid-cols-2 gap-3">
                    {(["minimumBid", "maximumBid"] as const).map((key) => (
                      <Field
                        key={key}
                        data-invalid={errors.has("bidBounds") || undefined}
                        className="gap-1.5"
                      >
                        <FieldLabel htmlFor={`${fieldPrefix}preview-ai-${key}`} className="text-xs">
                          {t(`review.aiActions.${key}`)}
                        </FieldLabel>
                        <MoneyInput
                          id={`${fieldPrefix}preview-ai-${key}`}
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={edit[key]}
                          disabled={!canEditBids}
                          aria-label={t(`review.aiActions.${key}`)}
                          aria-invalid={errors.has("bidBounds") || undefined}
                          onChange={(event) => update(key, event.target.value)}
                        />
                      </Field>
                    ))}
                    <p className="col-span-2 text-[11px] leading-4 text-muted-foreground">
                      {t("review.preview.ai.bidHelp")}
                    </p>
                    {bidBoundsSuggestion ? (
                      <p className="col-span-2 text-[11px] leading-4 text-muted-foreground">
                        {t(`review.preview.ai.bidSuggestion.${bidBoundsSuggestion.source}`)}
                      </p>
                    ) : null}
                    {errors.has("bidBounds") ? (
                      <p role="alert" className="col-span-2 text-xs text-destructive">
                        {t("review.aiActions.bidRangeRequired")}
                      </p>
                    ) : null}
                  </FieldGroup>
                ) : null}
                {group.key === "boost" ? (
                  <FieldGroup className="gap-3">
                    <FieldGroup className="grid grid-cols-2 gap-3">
                      {(
                        [
                          {
                            key: "boostLimit",
                            error: "budgetBoostPercentLimit",
                            label: "boostLimit",
                            max: 100,
                          },
                          {
                            key: "boostDuration",
                            error: "budgetBoostDurationDays",
                            label: "boostDuration",
                            max: 7,
                          },
                        ] as const
                      ).map((field) => (
                        <Field
                          key={field.key}
                          data-invalid={errors.has(field.error) || undefined}
                          className="gap-1.5"
                        >
                          <FieldLabel
                            htmlFor={`${fieldPrefix}preview-ai-${field.key}`}
                            className="text-xs"
                          >
                            {t(`review.aiActions.${field.label}`)}
                          </FieldLabel>
                          <Input
                            id={`${fieldPrefix}preview-ai-${field.key}`}
                            size="sm"
                            type="number"
                            min="1"
                            max={field.max}
                            step="1"
                            value={edit[field.key]}
                            disabled={!canEditBoost}
                            aria-label={t(`review.aiActions.${field.label}`)}
                            aria-invalid={errors.has(field.error) || undefined}
                            onChange={(event) => update(field.key, event.target.value)}
                          />
                          {errors.has(field.error) ? (
                            <p role="alert" className="text-xs text-destructive">
                              {t(
                                `review.aiActions.${field.label === "boostLimit" ? "boostLimitInvalid" : "boostDurationInvalid"}`,
                              )}
                            </p>
                          ) : null}
                        </Field>
                      ))}
                    </FieldGroup>
                  </FieldGroup>
                ) : null}
                {group.key === "harvest" || group.key === "negative" ? (
                  <FieldGroup>
                    <Field
                      data-invalid={
                        errors.has(
                          group.key === "harvest" ? "harvestExclusions" : "negativeExclusions",
                        ) || undefined
                      }
                    >
                      <FieldLabel
                        className="text-xs"
                        htmlFor={`${fieldPrefix}preview-ai-${group.key}-exclusions`}
                      >
                        {t(
                          `review.aiActions.${group.key === "harvest" ? "harvestExclusions" : "negativeExclusions"}`,
                        )}
                      </FieldLabel>
                      <Textarea
                        id={`${fieldPrefix}preview-ai-${group.key}-exclusions`}
                        size="sm"
                        className="min-h-[64px]"
                        value={
                          group.key === "harvest" ? edit.harvestExclusions : edit.negativeExclusions
                        }
                        disabled={!group.actions.some(canEditAction)}
                        aria-invalid={
                          errors.has(
                            group.key === "harvest" ? "harvestExclusions" : "negativeExclusions",
                          ) || undefined
                        }
                        onChange={(event) =>
                          update(
                            group.key === "harvest" ? "harvestExclusions" : "negativeExclusions",
                            event.target.value,
                          )
                        }
                      />
                      <FieldDescription className="text-[11px] leading-4">
                        {t("review.aiActions.exclusionsHelp")}
                      </FieldDescription>
                      {errors.has(
                        group.key === "harvest" ? "harvestExclusions" : "negativeExclusions",
                      ) ? (
                        <p role="alert" className="text-xs text-destructive">
                          {t("review.aiActions.exclusionsInvalid")}
                        </p>
                      ) : null}
                    </Field>
                  </FieldGroup>
                ) : null}
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
      {capabilityStatus === "unavailable" ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <p>{t("review.preview.ai.capabilityUnavailable")}</p>
          {onRetryCapabilities ? (
            <Button size="sm" variant="outline" onClick={onRetryCapabilities}>
              {t("review.retry")}
            </Button>
          ) : null}
        </div>
      ) : null}
      {saveError ? (
        <p role="alert" className="text-xs text-destructive">
          {t("review.aiActions.saveFailed")}
        </p>
      ) : null}
      {dirty && !immediate ? (
        <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
          <Button size="sm" variant="outline" disabled={saving} onClick={cancel}>
            {t("review.preview.ai.cancel")}
          </Button>
          <Button size="sm" loading={saving} disabled={!editable} onClick={() => void save()}>
            {t("review.preview.ai.save")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

interface BoostErrors {
  limit: boolean;
  duration: boolean;
}
const noBoostErrors: BoostErrors = { limit: false, duration: false };

function exclusionsFromText(value: string): string[] {
  return value
    .split(/[\n,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

/** Shared fields configure per-action consent; persistence and execution belong to the caller. */
export function AiActionPolicyFields(props: AiActionPolicyFieldsProps) {
  return props.variant === "preview" ? (
    <PreviewPolicyFields {...props} />
  ) : (
    <ImmediatePolicyFields {...props} />
  );
}

function ImmediatePolicyFields({
  policy,
  capabilities,
  capabilityStatus = capabilities ? "ready" : "unavailable",
  disabled = false,
  onPolicyChange,
}: AiActionPolicyFieldsProps) {
  const { t } = useTranslation("smartCreation");
  const [minimumBid, setMinimumBid] = useState(policy?.bidBounds?.min.toString() ?? "");
  const [maximumBid, setMaximumBid] = useState(policy?.bidBounds?.max.toString() ?? "");
  const [boostLimit, setBoostLimit] = useState((policy?.budgetBoostPercentLimit ?? 10).toString());
  const [boostDuration, setBoostDuration] = useState(
    (policy?.budgetBoostDurationDays ?? 1).toString(),
  );
  const [harvestExclusions, setHarvestExclusions] = useState(
    policy?.harvestExclusions.join("\n") ?? "",
  );
  const [negativeExclusions, setNegativeExclusions] = useState(
    policy?.negativeExclusions.join("\n") ?? "",
  );
  const [bidError, setBidError] = useState(false);
  const [boostErrors, setBoostErrors] = useState(noBoostErrors);
  const [exclusionError, setExclusionError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saving, setSaving] = useState(false);
  const editable = !!policy && !!onPolicyChange && !disabled && !saving;
  const canStageAction = (action: AiActionIdV1): boolean => {
    const matches = capabilities?.filter((capability) => capability.action === action) ?? [];
    return !!(
      editable &&
      capabilityStatus === "ready" &&
      matches.length === 1 &&
      matches[0]?.state === "supported"
    );
  };
  const canEditBids = bidActions.some(canStageAction);
  const canEditBoost = canStageAction("budgetBoost");
  const canEditExclusions = groups[3]?.actions.some(canStageAction) ?? false;
  const storedHarvestExclusions = policy?.harvestExclusions.join("\n") ?? "";
  const storedNegativeExclusions = policy?.negativeExclusions.join("\n") ?? "";

  useEffect(() => {
    setMinimumBid(policy?.bidBounds?.min.toString() ?? "");
    setMaximumBid(policy?.bidBounds?.max.toString() ?? "");
    setBidError(false);
  }, [policy?.bidBounds?.min, policy?.bidBounds?.max]);
  useEffect(() => {
    setBoostLimit((policy?.budgetBoostPercentLimit ?? 10).toString());
    setBoostDuration((policy?.budgetBoostDurationDays ?? 1).toString());
    setBoostErrors(noBoostErrors);
  }, [policy?.budgetBoostPercentLimit, policy?.budgetBoostDurationDays]);
  useEffect(() => {
    setHarvestExclusions(storedHarvestExclusions);
    setNegativeExclusions(storedNegativeExclusions);
  }, [storedHarvestExclusions, storedNegativeExclusions]);

  async function stagePolicy(
    actions: AiActionFlagsV1,
    settings: "bid" | "boost" | "exclusions" | null,
  ): Promise<void> {
    if (!editable || !policy || !onPolicyChange) return;
    const next = {
      ...policy,
      revision: policy.revision + 1,
      actions,
      approvalRiskThreshold: "all",
      dryRun: policy.source === "smart_creation" ? true : policy.dryRun,
    };
    if (settings === "bid") {
      const min = Number(minimumBid);
      const max = Number(maximumBid);
      const invalid =
        minimumBid.trim() === "" ||
        maximumBid.trim() === "" ||
        !Number.isFinite(min) ||
        !Number.isFinite(max) ||
        min <= 0 ||
        max < min;
      setBidError(invalid);
      if (invalid) return;
      next.bidBounds = { min, max };
    }
    if (settings === "boost") {
      const limit = Number(boostLimit);
      const duration = Number(boostDuration);
      const errors = {
        limit: boostLimit.trim() === "" || !Number.isFinite(limit) || limit < 1 || limit > 100,
        duration:
          boostDuration.trim() === "" ||
          !Number.isInteger(duration) ||
          duration < 1 ||
          duration > 7,
      };
      setBoostErrors(errors);
      if (Object.values(errors).some(Boolean)) return;
      next.budgetBoostPercentLimit = limit;
      next.budgetBoostDurationDays = duration;
    }
    if (settings === "exclusions") {
      next.harvestExclusions = exclusionsFromText(harvestExclusions);
      next.negativeExclusions = exclusionsFromText(negativeExclusions);
    }
    const parsed = AiActionPolicyV1Schema.safeParse(next);
    if (!parsed.success) {
      if (settings === "bid") setBidError(true);
      else if (settings === "exclusions") setExclusionError(true);
      else setSaveError(true);
      return;
    }
    setSaveError(false);
    setExclusionError(false);
    setSaving(true);
    try {
      const accepted = await onPolicyChange(parsed.data);
      if (!accepted) {
        if (settings === "bid") setBidError(true);
        else setSaveError(true);
      }
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  }

  async function stageAction(action: AiActionIdV1, enabled: boolean): Promise<void> {
    if (!canStageAction(action) || !policy) return;
    const actions = {
      ...policy.actions,
      [action]: enabled,
      rapidTesting: false,
      bidDayparting: false,
      budgetDayparting: false,
      reallocation: false,
    };
    // Opting out must always work, even when an unsaved numeric edit is invalid.
    const settings = enabled
      ? bidActions.includes(action)
        ? "bid"
        : action === "budgetBoost"
          ? "boost"
          : null
      : null;
    await stagePolicy(actions, settings);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
        {groups.map((group) => (
          <FieldSet key={group.key} className="gap-3 rounded-lg border border-line p-4.5">
            <FieldLegend variant="label">{t(`review.aiActions.${group.key}`)}</FieldLegend>
            <FieldGroup className="gap-3">
              {group.actions.map((action) => (
                <Field
                  key={action}
                  orientation="horizontal"
                  data-disabled={!canStageAction(action) || undefined}
                >
                  <Checkbox
                    id={`review-ai-${action}`}
                    checked={policy?.actions[action] ?? false}
                    disabled={!canStageAction(action)}
                    onCheckedChange={(checked) => void stageAction(action, checked)}
                  />
                  <FieldLabel htmlFor={`review-ai-${action}`}>
                    {t(`review.aiActions.${action}`)}
                  </FieldLabel>
                </Field>
              ))}
            </FieldGroup>
            {group.key === "bidding" ? (
              <FieldGroup className="grid grid-cols-2 gap-3">
                <Field
                  data-invalid={bidError || undefined}
                  data-disabled={!canEditBids || undefined}
                >
                  <FieldLabel htmlFor="review-ai-minimum-bid">
                    {t("review.aiActions.minimumBid")}
                  </FieldLabel>
                  <Input
                    id="review-ai-minimum-bid"
                    size="sm"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={minimumBid}
                    onChange={(event) => setMinimumBid(event.target.value)}
                    disabled={!canEditBids}
                    aria-label={t("review.aiActions.minimumBid")}
                    aria-invalid={bidError || undefined}
                    aria-describedby={bidError ? "review-ai-bid-error" : undefined}
                  />
                </Field>
                <Field
                  data-invalid={bidError || undefined}
                  data-disabled={!canEditBids || undefined}
                >
                  <FieldLabel htmlFor="review-ai-maximum-bid">
                    {t("review.aiActions.maximumBid")}
                  </FieldLabel>
                  <Input
                    id="review-ai-maximum-bid"
                    size="sm"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={maximumBid}
                    onChange={(event) => setMaximumBid(event.target.value)}
                    disabled={!canEditBids}
                    aria-label={t("review.aiActions.maximumBid")}
                    aria-invalid={bidError || undefined}
                    aria-describedby={bidError ? "review-ai-bid-error" : undefined}
                  />
                </Field>
                {bidError ? (
                  <p
                    id="review-ai-bid-error"
                    className="col-span-2 text-xs text-destructive"
                    role="alert"
                  >
                    {t("review.aiActions.bidRangeRequired")}
                  </p>
                ) : null}
                {bidActions.some((action) => policy?.actions[action]) ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="col-span-2 justify-self-start"
                    disabled={!canEditBids}
                    onClick={() => policy && void stagePolicy(policy.actions, "bid")}
                  >
                    {t("review.aiActions.applyBidRange")}
                  </Button>
                ) : null}
              </FieldGroup>
            ) : null}
            {group.key === "budget" ? (
              <FieldGroup className="gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    data-invalid={boostErrors.limit || undefined}
                    data-disabled={!canEditBoost || undefined}
                  >
                    <FieldLabel htmlFor="review-ai-boost-limit">
                      {t("review.aiActions.boostLimit")}
                    </FieldLabel>
                    <Input
                      id="review-ai-boost-limit"
                      size="sm"
                      type="number"
                      min="1"
                      max="100"
                      step="1"
                      value={boostLimit}
                      onChange={(event) => setBoostLimit(event.target.value)}
                      disabled={!canEditBoost}
                      aria-label={t("review.aiActions.boostLimit")}
                      aria-invalid={boostErrors.limit || undefined}
                      aria-describedby={
                        boostErrors.limit ? "review-ai-boost-limit-error" : undefined
                      }
                    />
                    {boostErrors.limit ? (
                      <p
                        id="review-ai-boost-limit-error"
                        className="text-xs text-destructive"
                        role="alert"
                      >
                        {t("review.aiActions.boostLimitInvalid")}
                      </p>
                    ) : null}
                  </Field>
                  <Field
                    data-invalid={boostErrors.duration || undefined}
                    data-disabled={!canEditBoost || undefined}
                  >
                    <FieldLabel htmlFor="review-ai-boost-duration">
                      {t("review.aiActions.boostDuration")}
                    </FieldLabel>
                    <Input
                      id="review-ai-boost-duration"
                      size="sm"
                      type="number"
                      min="1"
                      max="7"
                      step="1"
                      value={boostDuration}
                      onChange={(event) => setBoostDuration(event.target.value)}
                      disabled={!canEditBoost}
                      aria-label={t("review.aiActions.boostDuration")}
                      aria-invalid={boostErrors.duration || undefined}
                      aria-describedby={
                        boostErrors.duration ? "review-ai-boost-duration-error" : undefined
                      }
                    />
                    {boostErrors.duration ? (
                      <p
                        id="review-ai-boost-duration-error"
                        className="text-xs text-destructive"
                        role="alert"
                      >
                        {t("review.aiActions.boostDurationInvalid")}
                      </p>
                    ) : null}
                  </Field>
                </div>
                {policy?.actions.budgetBoost ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="self-start"
                    disabled={!canEditBoost}
                    onClick={() => void stagePolicy(policy.actions, "boost")}
                  >
                    {t("review.aiActions.applyBoostSettings")}
                  </Button>
                ) : null}
              </FieldGroup>
            ) : null}
            {group.key === "targeting" ? (
              <FieldGroup className="gap-3">
                <Field
                  data-invalid={exclusionError || undefined}
                  data-disabled={!canEditExclusions || undefined}
                >
                  <FieldLabel htmlFor="review-ai-harvest-exclusions">
                    {t("review.aiActions.harvestExclusions")}
                  </FieldLabel>
                  <Textarea
                    id="review-ai-harvest-exclusions"
                    size="sm"
                    value={harvestExclusions}
                    onChange={(event) => setHarvestExclusions(event.target.value)}
                    disabled={!canEditExclusions}
                    aria-label={t("review.aiActions.harvestExclusions")}
                    aria-invalid={exclusionError || undefined}
                  />
                </Field>
                <Field
                  data-invalid={exclusionError || undefined}
                  data-disabled={!canEditExclusions || undefined}
                >
                  <FieldLabel htmlFor="review-ai-negative-exclusions">
                    {t("review.aiActions.negativeExclusions")}
                  </FieldLabel>
                  <Textarea
                    id="review-ai-negative-exclusions"
                    size="sm"
                    value={negativeExclusions}
                    onChange={(event) => setNegativeExclusions(event.target.value)}
                    disabled={!canEditExclusions}
                    aria-label={t("review.aiActions.negativeExclusions")}
                    aria-invalid={exclusionError || undefined}
                  />
                  <FieldDescription>{t("review.aiActions.exclusionsHelp")}</FieldDescription>
                  {exclusionError ? (
                    <p className="text-xs text-destructive" role="alert">
                      {t("review.aiActions.exclusionsInvalid")}
                    </p>
                  ) : null}
                </Field>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="self-start"
                  disabled={!canEditExclusions}
                  onClick={() => policy && void stagePolicy(policy.actions, "exclusions")}
                >
                  {t("review.aiActions.applyExclusions")}
                </Button>
              </FieldGroup>
            ) : null}
          </FieldSet>
        ))}
      </div>
      {saveError ? (
        <p className="text-xs text-destructive" role="alert">
          {t("review.aiActions.saveFailed")}
        </p>
      ) : null}
    </div>
  );
}
